import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { ApiError } from 'src/error/ApiError';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { CalendarEvent } from 'src/meetings/models';
import type { MeetingScope } from 'src/meetings/scope';

type AttendeeDecision = { email: string; isAttendee: boolean };

export const useAttendeeReconciliation = (eventId: string, scope?: MeetingScope) => {
  const queryClient = useQueryClient();
  const eventQueryKey = ['calendar-events', eventId];

  return useMutation<{ data: CalendarEvent }, Error, { decisions: AttendeeDecision[] }>({
    mutationFn: ({ decisions }) =>
      api.post<CalendarEvent>(
        `calendar-events/${eventId}/attendee-reconciliation`,
        { decisions },
        { expectedStatuses: [409] },
      ),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(eventQueryKey, data);
      void queryClient.invalidateQueries({ queryKey: eventQueryKey });
      if (scope) {
        void queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });
      }
    },
    onError: error => {
      if (isStaleReconciliation(error)) {
        void queryClient.invalidateQueries({ queryKey: eventQueryKey });
      }
    },
  });
};

export function isStaleReconciliation(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}
